/**
 * Google Drive 連携クライアント（ブラウザ完結）。
 *
 * <p>Google Identity Services (GIS) の Token Model で `drive.appdata` スコープの
 * アクセストークンをブラウザで取得し、Drive REST v3 を直接 `fetch` して
 * `appDataFolder` に `tax-assistant-backup.json` をバックアップ／復元する。
 * クライアントシークレットは使用しない（パブリッククライアント）。</p>
 *
 * <p>アクセストークンは約1時間で失効する。refresh token は XSS リスクを避けるため
 * ブラウザへ永続保存せず（localStorage / sessionStorage / Cookie 不使用）、失効時は
 * 再認証を促す（憲章 IV）。PII はサーバを経由せずユーザーの Drive 内に留まる。</p>
 *
 * <p>GIS スクリプト（`https://accounts.google.com/gsi/client`）は第三者へのリクエストを
 * 最小化するため、初回の認証操作時に動的挿入で遅延ロードする。</p>
 *
 * 出典:
 * - Google Identity Services Token Model
 *   https://developers.google.com/identity/oauth2/web/guides/use-token-model
 * - GIS JavaScript リファレンス（initTokenClient / requestAccessToken / revoke）
 *   https://developers.google.com/identity/oauth2/web/reference/js-reference
 * - Drive API v3 Files
 *   https://developers.google.com/drive/api/reference/rest/v3/files
 */

import { PayrollSession } from '../types/PayrollTypes';

export interface GoogleDriveBackupInfo {
  authenticated: boolean;
  hasBackup?: boolean;
  lastModified?: string;
  fileSize?: number;
  /** バックアップ情報の取得に失敗した場合のエラー（401 失効は authenticated:false）。 */
  error?: string;
}

export interface BackupResult {
  success: boolean;
  message?: string;
  error?: string;
}

export interface RestoreResult {
  success: boolean;
  data?: PayrollSession;
  backupDate?: string;
  version?: string;
  error?: string;
}

/** バックアップファイル名（旧サーバ実装と同一）。 */
const BACKUP_FILE_NAME = 'tax-assistant-backup.json';
const BACKUP_VERSION = '1.0';
const DRIVE_APPDATA_SCOPE = 'https://www.googleapis.com/auth/drive.appdata';
const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const DRIVE_FILES_URL = 'https://www.googleapis.com/drive/v3/files';
const DRIVE_UPLOAD_URL = 'https://www.googleapis.com/upload/drive/v3/files';
/** 失効直前の境界を避けるため、1分の余裕を持って失効扱いにする。 */
const TOKEN_EXPIRY_SKEW_MS = 60 * 1000;
const AUTH_REQUIRED_MESSAGE =
  'Google Drive の認証が必要です。認証するか、時間が経過している場合は再度認証してください。';
const AUTH_EXPIRED_MESSAGE =
  'Google Drive の認証が失効しました。再度認証してください。';

interface DriveFile {
  id: string;
  name?: string;
  modifiedTime?: string;
  size?: string;
}

interface GoogleTokenResponse {
  access_token?: string;
  expires_in?: number;
  scope?: string;
  token_type?: string;
  error?: string;
  error_description?: string;
}

interface GoogleTokenClient {
  requestAccessToken(overrideConfig?: { prompt?: string }): void;
}

interface GoogleTokenClientConfig {
  client_id: string;
  scope: string;
  callback: (response: GoogleTokenResponse) => void;
  error_callback?: (error: { type?: string; message?: string }) => void;
}

interface GoogleRevocationResponse {
  successful?: boolean;
  error?: string;
}

interface GoogleOAuth2 {
  initTokenClient(config: GoogleTokenClientConfig): GoogleTokenClient;
  revoke(
    token: string,
    callback?: (response: GoogleRevocationResponse) => void
  ): void;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: GoogleOAuth2;
      };
    };
  }
}

// メモリ上のみでトークンを保持する（永続化しない）。
let accessToken: string | null = null;
let accessTokenExpiresAt = 0;

// GIS スクリプトの遅延ロードは一度だけ行う。
let gisScriptPromise: Promise<void> | null = null;

function getClientId(): string {
  return process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
}

function getOAuth2(): GoogleOAuth2 | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const google = window.google;
  return (google && google.accounts && google.accounts.oauth2) || null;
}

/**
 * GIS スクリプトの読み込み状態をリセットする（`<script>` を除去しキャッシュを破棄）。
 * 読み込み失敗や、ロード後に oauth2 が得られなかった場合の再試行に使う。
 */
function resetGisScriptState(): void {
  gisScriptPromise = null;
  if (typeof document !== 'undefined') {
    document
      .querySelectorAll(`script[src="${GIS_SCRIPT_SRC}"]`)
      .forEach((element) => element.remove());
  }
}

/**
 * GIS スクリプトを動的に読み込む。
 * 第三者リクエストを認証操作のタイミングまで遅延させる。
 * 読み込みに失敗した場合は `<script>` を除去して状態をリセットし、
 * 次の呼び出しで新しい要素を使って再試行できるようにする。
 */
function loadGisScript(): Promise<void> {
  if (getOAuth2()) {
    return Promise.resolve();
  }
  if (gisScriptPromise) {
    return gisScriptPromise;
  }
  if (typeof document === 'undefined') {
    return Promise.reject(
      new Error('Google Identity Services を読み込めませんでした')
    );
  }

  gisScriptPromise = new Promise<void>((resolve, reject) => {
    const rejectWithLoadError = () => {
      // 失敗した要素を残すと再試行時に settle しないため、必ず除去する。
      resetGisScriptState();
      reject(
        new Error(
          'Google Identity Services を読み込めませんでした。ネットワークを確認して再試行してください。'
        )
      );
    };

    // 過去の失敗などで残っている要素を除去し、常に新しい要素を生成する。
    document
      .querySelectorAll(`script[src="${GIS_SCRIPT_SRC}"]`)
      .forEach((element) => element.remove());

    const script = document.createElement('script');
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => rejectWithLoadError();
    document.head.appendChild(script);
  });

  return gisScriptPromise;
}

/**
 * GIS スクリプトを先読みする（認証ボタンの hover / pointerdown 等から呼ぶ）。
 * クリック時の遅延を減らし、`requestAccessToken()` をユーザー操作に近いタイミングで
 * 呼べるようにする。失敗しても throw しない（クリック時に再試行される）。
 */
export function preloadGoogleIdentityServices(): void {
  if (!getClientId()) {
    return;
  }
  loadGisScript().catch(() => {
    // 先読みの失敗は無視する。
  });
}

function setAccessToken(token: string, expiresInSeconds?: number): void {
  accessToken = token;
  const lifetimeMs = (expiresInSeconds || 3600) * 1000;
  accessTokenExpiresAt = Date.now() + lifetimeMs;
}

function clearAccessToken(): void {
  accessToken = null;
  accessTokenExpiresAt = 0;
}

/** 有効なトークンがあれば返し、なければ null（失効分は破棄）。 */
function requireToken(): string | null {
  if (!accessToken || Date.now() >= accessTokenExpiresAt - TOKEN_EXPIRY_SKEW_MS) {
    clearAccessToken();
    return null;
  }
  return accessToken;
}

/**
 * 認証失効を表すエラーを生成する。
 * `target: es5` では `Error` のサブクラスが `instanceof` で判定できないため、
 * フラグで判定する。
 */
function unauthorizedError(): Error {
  const error = new Error(AUTH_EXPIRED_MESSAGE);
  (error as { unauthorized?: boolean }).unauthorized = true;
  return error;
}

function isUnauthorizedError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { unauthorized?: boolean }).unauthorized === true
  );
}

function authHeaders(token: string): { Authorization: string } {
  return { Authorization: `Bearer ${token}` };
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

/** ローカル時刻の ISO 8601 文字列（旧サーバの ISO_LOCAL_DATE_TIME 互換）。 */
function localIsoDateTime(date: Date): string {
  return (
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}` +
    `T${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`
  );
}

async function findBackupFile(token: string): Promise<DriveFile | null> {
  const query = encodeURIComponent(`name='${BACKUP_FILE_NAME}'`);
  const url =
    `${DRIVE_FILES_URL}?spaces=appDataFolder&q=${query}` +
    '&fields=files(id,name,modifiedTime,size)';
  const response = await fetch(url, { headers: authHeaders(token) });
  if (response.status === 401) {
    throw unauthorizedError();
  }
  if (!response.ok) {
    throw new Error('バックアップファイルの検索に失敗しました');
  }
  const result: { files?: DriveFile[] } = await response.json();
  const files = result.files || [];
  return files.length > 0 ? files[0] : null;
}

async function createBackupFile(token: string, content: string): Promise<void> {
  const boundary = `tax_assistant_${Date.now()}`;
  const metadata = JSON.stringify({
    name: BACKUP_FILE_NAME,
    parents: ['appDataFolder'],
  });
  const body =
    `--${boundary}\r\n` +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    `${metadata}\r\n` +
    `--${boundary}\r\n` +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    `${content}\r\n` +
    `--${boundary}--`;

  const response = await fetch(
    `${DRIVE_UPLOAD_URL}?uploadType=multipart&fields=id,name`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(token),
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body,
    }
  );
  if (response.status === 401) {
    throw unauthorizedError();
  }
  if (!response.ok) {
    throw new Error('バックアップファイルの作成に失敗しました');
  }
}

async function updateBackupFile(
  token: string,
  fileId: string,
  content: string
): Promise<void> {
  const response = await fetch(
    `${DRIVE_UPLOAD_URL}/${encodeURIComponent(fileId)}?uploadType=media`,
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(token),
        'Content-Type': 'application/json; charset=UTF-8',
      },
      body: content,
    }
  );
  if (response.status === 401) {
    throw unauthorizedError();
  }
  if (!response.ok) {
    throw new Error('バックアップファイルの更新に失敗しました');
  }
}

async function downloadBackupFile(token: string, fileId: string): Promise<string> {
  const response = await fetch(
    `${DRIVE_FILES_URL}/${encodeURIComponent(fileId)}?alt=media`,
    { headers: authHeaders(token) }
  );
  if (response.status === 401) {
    throw unauthorizedError();
  }
  if (!response.ok) {
    throw new Error('バックアップファイルの取得に失敗しました');
  }
  return response.text();
}

function toErrorMessage(error: unknown, fallback: string): string {
  if (isUnauthorizedError(error)) {
    return AUTH_EXPIRED_MESSAGE;
  }
  return error instanceof Error ? error.message : fallback;
}

export class GoogleDriveClient {
  /** GIS のクライアント ID が設定されているか。 */
  static hasClientId(): boolean {
    return getClientId().length > 0;
  }

  /** メモリ上のトークンが有効か。 */
  static isAuthenticated(): boolean {
    return requireToken() !== null;
  }

  /**
   * GIS Token Model でアクセストークンを取得する（ユーザー操作起点）。
   * client_secret は使用しない。GIS スクリプトはこの時点で遅延ロードする。
   */
  static async startAuthentication(): Promise<void> {
    const clientId = getClientId();
    if (!clientId) {
      throw new Error('REACT_APP_GOOGLE_CLIENT_ID が設定されていません');
    }

    await loadGisScript();

    const oauth2 = getOAuth2();
    if (!oauth2) {
      // スクリプトはロードされたが oauth2 が提供されなかった場合。
      // 状態をリセットして、次回の認証操作で新しい script を生成できるようにする。
      resetGisScriptState();
      throw new Error(
        'Google Identity Services を読み込めませんでした。ページを再読み込みしてください。'
      );
    }

    return new Promise<void>((resolve, reject) => {
      const tokenClient = oauth2.initTokenClient({
        client_id: clientId,
        scope: DRIVE_APPDATA_SCOPE,
        callback: (response) => {
          if (response.error || !response.access_token) {
            reject(
              new Error(
                response.error_description ||
                  response.error ||
                  'Google Drive の認証に失敗しました'
              )
            );
            return;
          }
          setAccessToken(response.access_token, response.expires_in);
          resolve();
        },
        error_callback: (error) => {
          // ポップアップブロック・ウィンドウ閉じ・同意拒否など。
          clearAccessToken();
          reject(new Error(error.message || 'Google Drive の認証がキャンセルされました'));
        },
      });
      tokenClient.requestAccessToken();
    });
  }

  /**
   * バックアップファイルの有無・最終更新・サイズを取得する。
   * 未認証なら `{ authenticated: false }`。認証済みで取得に失敗した場合は
   * `hasBackup` を偽装せず `error` を返す。
   */
  static async getBackupInfo(): Promise<GoogleDriveBackupInfo> {
    const token = requireToken();
    if (!token) {
      return { authenticated: false };
    }
    try {
      const file = await findBackupFile(token);
      if (!file) {
        return { authenticated: true, hasBackup: false };
      }
      return {
        authenticated: true,
        hasBackup: true,
        lastModified: file.modifiedTime,
        fileSize: file.size !== undefined ? Number(file.size) : undefined,
      };
    } catch (error) {
      if (isUnauthorizedError(error)) {
        clearAccessToken();
        return { authenticated: false };
      }
      console.error('バックアップ情報取得エラー:', error);
      return {
        authenticated: true,
        error: toErrorMessage(error, 'バックアップ情報の取得に失敗しました'),
      };
    }
  }

  /** 給与セッションを `appDataFolder` にバックアップする（存在すれば更新）。 */
  static async backupData(payrollData: PayrollSession): Promise<BackupResult> {
    const token = requireToken();
    if (!token) {
      return { success: false, error: AUTH_REQUIRED_MESSAGE };
    }
    try {
      const content = JSON.stringify({
        version: BACKUP_VERSION,
        backupDate: localIsoDateTime(new Date()),
        sessionId: payrollData.sessionId || '',
        data: payrollData,
      });
      const existing = await findBackupFile(token);
      if (existing) {
        await updateBackupFile(token, existing.id, content);
      } else {
        await createBackupFile(token, content);
      }
      return { success: true, message: 'データのバックアップが完了しました' };
    } catch (error) {
      if (isUnauthorizedError(error)) {
        clearAccessToken();
      }
      console.error('バックアップエラー:', error);
      return { success: false, error: toErrorMessage(error, 'バックアップに失敗しました') };
    }
  }

  /** `appDataFolder` のバックアップから給与セッションを復元する。 */
  static async restoreData(): Promise<RestoreResult> {
    const token = requireToken();
    if (!token) {
      return { success: false, error: AUTH_REQUIRED_MESSAGE };
    }
    try {
      const file = await findBackupFile(token);
      if (!file) {
        return { success: false, error: 'バックアップファイルが見つかりません' };
      }
      const content = await downloadBackupFile(token, file.id);
      const backup: {
        data?: PayrollSession;
        backupDate?: string;
        version?: string;
      } = JSON.parse(content);
      return {
        success: true,
        data: backup.data,
        backupDate: backup.backupDate,
        version: backup.version,
      };
    } catch (error) {
      if (isUnauthorizedError(error)) {
        clearAccessToken();
      }
      console.error('リストアエラー:', error);
      return { success: false, error: toErrorMessage(error, 'リストアに失敗しました') };
    }
  }

  /**
   * アクセストークンを破棄し、Google 側でも失効させる。
   * バックアップファイル自体は削除しない（旧サーバの認証解除と同等）。
   * `invalid_token`（すでに無効）は成功として扱い、それ以外の失効失敗はエラーを返す。
   */
  static revokeAuthentication(): Promise<BackupResult> {
    const token = accessToken;
    clearAccessToken();

    if (!token) {
      return Promise.resolve({
        success: false,
        message: '認証情報が見つかりませんでした',
      });
    }

    const oauth2 = getOAuth2();
    if (!oauth2) {
      // ローカルのトークンは破棄済み。GIS 未ロード時は Google 側の失効のみスキップする。
      return Promise.resolve({
        success: true,
        message: '認証情報を破棄しました（Google 側の失効は確認できませんでした）',
      });
    }

    return new Promise<BackupResult>((resolve) => {
      try {
        oauth2.revoke(token, (response) => {
          if (
            response &&
            response.successful === false &&
            response.error &&
            response.error !== 'invalid_token'
          ) {
            resolve({
              success: false,
              error: response.error || '認証の失効に失敗しました',
            });
            return;
          }
          resolve({ success: true, message: '認証を解除しました' });
        });
      } catch (error) {
        console.warn('アクセストークンの失効に失敗しました:', error);
        resolve({
          success: true,
          message: '認証情報を破棄しました（Google 側の失効は確認できませんでした）',
        });
      }
    });
  }
}
